import { create } from 'zustand'
import { createComputed } from 'zustand-computed'
import { AnimationState, GAME_RISK, GAME_TYPE, GameStatus } from './types'
import { BigNumber, BigNumberInBase, BigNumberInWei } from '@injectivelabs/utils'
import { getTokenByCurrency, getTokenByDenom } from '@/services/token-service'
import { SupportedCurrencies } from '@ninjablaze/sdk-ts'

type FormFields = {
  amount: string
  risk: GAME_RISK
  gameType: GAME_TYPE
  denom: string
  maxValue: number
  minValue: number
}

export const currenciesSettings = {
  supported: [SupportedCurrencies.INJ, SupportedCurrencies.USDC, SupportedCurrencies.NBZ, SupportedCurrencies.SOL, SupportedCurrencies.SHU],
  minValue: {
    [SupportedCurrencies.INJ]: 0.01,
    [SupportedCurrencies.USDC]: 0.1,
    [SupportedCurrencies.USDT]: 0.1,
    [SupportedCurrencies.NBZ]: 10,
    [SupportedCurrencies.SHU]: 1,
    [SupportedCurrencies.SOL]: 0.001,
  },
  maxValue: {
    [SupportedCurrencies.INJ]: 1,
    [SupportedCurrencies.NBZ]: 1000,
    [SupportedCurrencies.SHU]: 100,
    [SupportedCurrencies.USDC]: 10,
    [SupportedCurrencies.USDT]: 10,
    [SupportedCurrencies.SOL]: 0.1,
  }
}

type GameConfig = {
  rtp: number
}

type RunesState = {
  gameId: string | null
  gameConfig: GameConfig
  isInitialized: boolean
  status: GameStatus
  isDrawing: boolean
  isCancelled: boolean




  setGameActive: () => void
  initialize: () => void
  handleEndGame: () => void
  boot: () => void

  formRevalidation: () => void

  setDenom: (denom: string) => void
  view: {
    gameResult: {
      items: number[],
      revealedCount: number,
      result: number,
    }
  }
  form: {
    errors: Array<string>
    fields: FormFields
    isDirty: boolean
  }


  setGameState(items: number[], withAnimation?: boolean): void

  setValue: <K extends keyof FormFields>(key: K, value: FormFields[K], setDirty?: boolean) => void

  setValues: <K extends keyof FormFields>({ }: Partial<Record<K, { value: FormFields[K]; setDirty: boolean }>>) => void
}

type ComputedStore = {
}


const computed = createComputed(
  (state: RunesState): ComputedStore => {
    return {
    }
  },
  { disableProxy: true, keys: ['form', 'gameConfig'] }
)

const intital = {
  gameId: null,
  gameConfig: {
    rtp: 0.97,
    maxWin: new BigNumberInBase(100000000).toWei(18),
    maxGameRound: 15,
  },
  bet: null,
  status: GameStatus.IDLE,
  isDrawing: false,
}

export const useRunesStore = create<RunesState>()(
  computed((set, get) => ({
    gameId: null,
    gameConfig: intital.gameConfig,
    isInitialized: false,
    status: GameStatus.IDLE,
    isDrawing: false,
    animationSpeed: 1,
    animationState: AnimationState.IDLE,

    gameResult: {
      items: [1, 2, 3, 4, 5],
      result: 0,
    },


    setGameActive: () =>
      set((state) => ({ status: GameStatus.ACTIVE })),
    handleEndGame: () =>
      set((state) => ({
        ...intital,
        gameConfig: state.gameConfig
      })),
    boot: () =>
      set({
        ...intital,
        isInitialized: false
        //   animationSide: { prev: RunesChoice.Heads, current: RunesChoice.Heads },
      }),
    initialize: () => set({ ...intital, isInitialized: true }),

    view: {
      gameResult: {
        items: [1, 2, 3, 4, 5],
        revealedCount: 0,
        result: 0,
      }
    },
    isCancelled: false,
    form: {
      errors: [],
      fields: {
        amount: '0.1',
        risk: GAME_RISK.MEDIUM,
        gameType: GAME_TYPE.MANUAL,
        denom: getTokenByCurrency(SupportedCurrencies.INJ).denom,
        minValue: currenciesSettings.minValue[SupportedCurrencies.INJ],
        maxValue: currenciesSettings.maxValue[SupportedCurrencies.INJ],
      },
      isDirty: false,
    },

    setGameState: async (items: any, withAnimation = true) => {

      set(state => ({
        view: {
          ...state.view,
          gameResult: {
            ...state.view.gameResult,
            items: [0, 0, 0, 0, 0],
            revealedCount: 0,
          }
        }
      }));

      if (!withAnimation) {
        return;
      }


      for (let i = 0; i < items.length; i++) {
        set(state => ({
          view: {
            ...state.view,
            gameResult: {
              ...state.view.gameResult,
              items: items.slice(0, state.view.gameResult.revealedCount + 1),
              revealedCount: state.view.gameResult.revealedCount + 1,
            }
          }
        }))

        await new Promise((resolve) => setTimeout(resolve, window.animationSpeed || 275));

      }
    },

    setDenom: (denom: string) => {
      const minValue = currenciesSettings.minValue[getTokenByDenom(denom).currency]

      set((state) => {
        const { fields, errors } = formValidation({
          amount: (minValue * 10).toString(),
          //parseFloat(state.form.fields.amount) < minValue ? minValue.toString() : state.form.fields.amount,
          gameType: state.form.fields.gameType,
          denom: denom,
          risk: state.form.fields.risk,
          minValue: state.form.fields.minValue,
          maxValue: state.form.fields.maxValue,
        })

        return {
          form: {
            errors,
            fields,
            isDirty: true,
          },
        }
      })
    },
    setValue: (key, value, setDirty = true) => {
      get().setValues({
        [key]: {
          value: value as any,
          setDirty,
        },
      })
    },
    formRevalidation: () => {
      const { fields, errors } = formValidation(get().form.fields)
      set({
        form: {
          fields,
          errors,
          isDirty: true,
        },
      })
    },
    setValues: (values) => {
      set((state) => {
        let newFields = {
          ...state.form.fields,
        }

        let isDirty = false
        for (const key in values) {
          newFields = {
            ...newFields,
            [key]: values[key]!.value,
          }

          isDirty = isDirty || values[key]!.setDirty
        }

        const { fields, errors } = formValidation(formParser(newFields))

        sessionStorage.setItem('runes-form', JSON.stringify(fields))

        return {
          form: {
            errors,
            fields,
            isDirty: !isDirty ? state.form.isDirty : true,
          },
        }
      })
    },
  }))
)

const formParser = (values: FormFields) => {
  const { amount } = values
  const token = getTokenByDenom(values.denom)
  const fields = {
    amount,
    minValue: currenciesSettings.minValue[token.currency]!,
    maxValue: currenciesSettings.maxValue[token.currency]!,
  }

  if (isNaN(Number(amount)) || Number(amount) < 0) {
    fields.amount = ''
    return { ...values, ...fields }
  }

  if (Number(amount) > fields.maxValue) {
    fields.amount = fields.maxValue.toString()
  }

  const regex = new RegExp(`^\\d+(\\.\\d{0,${token.precisions}})?$`, 'g')

  if (amount != '' && !regex.test(amount)) {
    fields.amount = Number(amount).toFixed(token.precisions)
  }

  return { ...values, ...fields }
}

const formValidation = (fields: FormFields) => {
  let { amount, minValue } = fields

  const errors = []

  if (amount.length === 0) {
    errors.push('Amount is required')
  }

  if (Number(amount) < minValue) {
    errors.push('Min amount is ' + minValue)
  }

  return {
    fields,
    errors,
  }
}
